<template>
  <fieldset class="YesNoComponent">
    <legend v-html="legend" />
    <div
      class="choices"
      :class="{ error: !!fieldValidationErrorMessage }"
    >
      <div
        v-for="(option, i) in options"
        :key="i"
        class="choice"
      >
        <input
          type="radio"
          :id="`${id}-${option.value}-yes-no`"
          :name="`${id}-yes-no`"
          :value="option.value"
          :checked="isSelected(option.value)"
          v-model="innerValue"
          @keydown.enter.prevent="innerValue = option.value"
        />

        <label :for="`${id}-${option.value}-yes-no`">
          {{ option.label }}
        </label>
      </div>
    </div>

    <div
      ref="hiddenFieldWrapper"
      class="invisible"
      aria-hidden="true"
    >
      <v-text-field
        ref="$field"
        :key="String(innerValue ?? '')"
        :id="`${id}-yes-no-value`"
        aria-hidden="true"
        :aria-describedby="undefined"
        tabindex="-1"
        v-model="innerValue"
        :rules="rules"
        :hide-details="true"
      />
    </div>

    <ErrorMessageComponent :messages="fieldValidationErrorMessage" />
  </fieldset>
</template>

<script setup lang="ts">
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '@/modules/core/composables/accesibilityComposable'
  import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
  import type { ValidationRuleType } from '@/modules/core/models/types'
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import ErrorMessageComponent from '../formFields/ErrorMessageComponent.vue'

  const { t } = useI18n()

  interface PropsInterface {
    id: string
    legend?: string
    modelValue: boolean | null
    rules?: ValidationRuleType[]
  }

  const props = withDefaults(defineProps<PropsInterface>(), {
    modelValue: null,
    rules: () => []
  })

  const options = [
    { label: t('common.choices.yes'), value: 1 },
    { label: t('common.choices.no'), value: 0 }
  ]

  const emit = defineEmits(['update:modelValue'])

  const innerValue = computed({
    get: () => Number(props.modelValue ?? -1),
    set: (value: number) => emit('update:modelValue', !!value)
  })

  function isSelected(value: number) {
    return value === innerValue.value
  }

  const { $field, fieldValidationErrorMessage } = useValidationError()

  const hiddenFieldWrapper = ref<HTMLElement | null>(null)
  let hiddenObserver: MutationObserver | null = null

  onMounted(() => {
    hiddenObserver = startDuplicatedLabelControl(hiddenFieldWrapper.value, {
      labelOrphanedInputs: true
    })
  })
  onUnmounted(() => endDuplicatedLabelControl(hiddenObserver))
</script>

<style lang="scss" scoped>
  .YesNoComponent {
    border: none;
    margin-top: $spacing-l;

    legend {
      font-family: $font-family-primary;
      font-size: $font-size-m;
      font-style: normal;
      font-weight: $font-weight-400;
      line-height: $line-height-100p;
      color: $color-neutral-dark;
      margin-bottom: $spacing-l;

      &:empty {
        margin: 0;
      }
    }

    .invisible {
      display: none;
    }

    .choices {
      display: flex;
      gap: $gap-m;

      .choice {
        flex: 1;

        label {
          cursor: pointer;
          height: $size-full;
          background-color: $color-neutral-lighter;
          border: solid 1px $color-neutral-light;
          padding: $spacing-xl $spacing-s $spacing-xl $spacing-xl;
          display: grid;
          align-items: center;
          grid-template-columns: auto auto 1fr;
          font-size: $font-size-l;
          font-weight: $font-weight-400;
          color: $color-neutral-dark;
        }

        &.error {
          label {
            border-color: $color-error;
            background-color: $color-error-200;
          }
        }

        input {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;

          &:focus-visible + label {
            box-shadow: 0 0 0 4px $color-focus;
          }

          &:checked + label {
            color: $color-primary;
            border: 1.5px solid $color-primary;
            border-radius: $b-radius-2;
            background-color: $color-primary-lighter;
          }
        }
      }
    }
  }
</style>
